import type { CSSProperties } from "react";

/** Self-contained SVG dial — no chart library, no external asset (so nothing to
 *  pay for or load over the network). A 270° arc from min→max with a coloured
 *  progress sweep and a value read-out in the hub. `zones` optionally recolours
 *  the sweep by band (e.g. SoC red<20, amber<50, green else). */
export function Gauge({
  value, min = 0, max = 100, unit = "", label, size = 132, color = "var(--teal)",
  zones, decimals = 0,
}: {
  value: number | null | undefined;
  min?: number; max?: number; unit?: string; label: string;
  size?: number; color?: string; decimals?: number;
  zones?: { upTo: number; color: string }[];
}) {
  const START = 135;          // degrees — bottom-left
  const SWEEP = 270;          // total arc
  const has = value != null && Number.isFinite(value);
  const clamped = has ? Math.min(max, Math.max(min, value as number)) : min;
  const frac = max > min ? (clamped - min) / (max - min) : 0;

  const arcColor = (() => {
    if (!zones) return color;
    for (const z of zones) if (clamped <= z.upTo) return z.color;
    return zones[zones.length - 1]?.color ?? color;
  })();

  const r = size / 2 - 12;
  const cx = size / 2;
  const cy = size / 2;
  const polar = (deg: number) => {
    const a = (deg * Math.PI) / 180;
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
  };
  // arc goes clockwise from START through SWEEP
  const arcPath = (fromFrac: number, toFrac: number) => {
    const a0 = START + SWEEP * fromFrac;
    const a1 = START + SWEEP * toFrac;
    const p0 = polar(a0);
    const p1 = polar(a1);
    const large = a1 - a0 > 180 ? 1 : 0;
    return `M ${p0.x} ${p0.y} A ${r} ${r} 0 ${large} 1 ${p1.x} ${p1.y}`;
  };

  const wrap: CSSProperties = { display: "flex", flexDirection: "column", alignItems: "center", gap: 2 };
  return (
    <div style={wrap}>
      <svg width={size} height={size * 0.82} viewBox={`0 0 ${size} ${size * 0.82}`} role="img" aria-label={label}>
        {/* track */}
        <path d={arcPath(0, 1)} fill="none" stroke="var(--line2)" strokeWidth={9} strokeLinecap="round" />
        {/* value sweep */}
        {has && frac > 0 && (
          <path d={arcPath(0, frac)} fill="none" stroke={arcColor} strokeWidth={9} strokeLinecap="round"
            style={{ transition: "stroke-dashoffset .4s, d .4s" }} />
        )}
        {/* hub read-out */}
        <text x={cx} y={cy - 2} textAnchor="middle" fontSize={size * 0.2} fontWeight={800}
          fill="var(--t-bright)" fontFamily="var(--mono, monospace)">
          {has ? (value as number).toFixed(decimals) : "—"}
        </text>
        <text x={cx} y={cy + size * 0.13} textAnchor="middle" fontSize={size * 0.088}
          fill="var(--t-dim)" letterSpacing="0.5">
          {unit}
        </text>
      </svg>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--t-mute)" }}>
        {label}
      </div>
    </div>
  );
}

/** Horizontal cell-balance bar: min..max span within a plausible cell range so
 *  imbalance is visible at a glance. Green tight, amber wide, red very wide. */
export function BalanceBar({ minMv, maxMv }: { minMv?: number | null; maxMv?: number | null }) {
  const LO = 2800, HI = 4200;           // Li-ion working window
  if (minMv == null || maxMv == null || minMv === 0 || maxMv === 0) {
    return <div style={{ fontSize: 12, color: "var(--t-dim)" }}>no cell data</div>;
  }
  const span = HI - LO;
  const l = Math.max(0, Math.min(1, (minMv - LO) / span)) * 100;
  const r = Math.max(0, Math.min(1, (maxMv - LO) / span)) * 100;
  const delta = maxMv - minMv;
  const col = delta <= 30 ? "var(--ok)" : delta <= 80 ? "var(--warn)" : "var(--bad)";
  return (
    <div>
      <div style={{ position: "relative", height: 10, borderRadius: 6, background: "var(--line2)", overflow: "hidden" }}>
        <div style={{ position: "absolute", left: `${l}%`, width: `${Math.max(2, r - l)}%`, top: 0, bottom: 0, background: col, borderRadius: 6 }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: "var(--t-mute)", marginTop: 3 }}>
        <span className="mono">{minMv} mV</span>
        <span className="mono" style={{ color: col, fontWeight: 700 }}>Δ {delta} mV</span>
        <span className="mono">{maxMv} mV</span>
      </div>
    </div>
  );
}
